import React, { useState, useEffect } from 'react';
import {
  Phone,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Shield,
  Globe,
  Plus,
  Trash2,
  Calendar,
  MapPin,
  Loader,
  X,
  Settings,
  BarChart3
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';

interface WhitelistedIP {
  id: string;
  ip_address: string;
  label: string;
  date_added: string;
  added_by?: string;
}

interface ServiceItem {
  pk: number;
  account_id: number;
  api_id: string;
  status: string;
  provision_status: string;
  item_type: string;
  quantity: number;
  metadata: any;
}

const LegacyVoice: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'whitelist' | 'settings'>('overview');
  const [whitelistedIPs, setWhitelistedIPs] = useState<WhitelistedIP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detectingIP, setDetectingIP] = useState(false);
  const [currentPublicIP, setCurrentPublicIP] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; ip: string } | null>(null);
  const [newIPLabel, setNewIPLabel] = useState('');
  const [manualIP, setManualIP] = useState('');
  const [useManualIP, setUseManualIP] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceInfo, setServiceInfo] = useState<ServiceItem | null>(null);
  const [totalExtensions, setTotalExtensions] = useState(0);

  // Get customer info from auth service
  const userInfo = authService.getCurrentUser();
  const customerId = userInfo?.account_id?.toString() || 'default';
  const customerName = userInfo?.account?.name || 'Your Organization';

  useEffect(() => {
    const initializeData = async () => {
      await loadServiceInfo();
      detectPublicIP();
    };
    initializeData();
  }, []);

  // Load IPs when service info becomes available
  useEffect(() => {
    if (serviceInfo?.pk) {
      loadWhitelistedIPs();
    }
  }, [serviceInfo]);

  const loadServiceInfo = async () => {
    try {
      const response = await apiService.getServices();
      const services = response.result || [];

      // Find the legacy voice service
      const legacyService = services.find((s: ServiceItem) => s.item_type === 'itp_voice_legacy');

      if (legacyService) {
        setServiceInfo(legacyService);
        setTotalExtensions(legacyService.quantity || 0);
      }
    } catch (error) {
      console.error('Failed to load service info:', error);
    }
  };

  const loadWhitelistedIPs = async () => {
    if (!serviceInfo?.pk) return;

    setLoading(true);
    try {
      const response = await apiService.getLegacyVoiceWhitelistedIPs(serviceInfo.pk.toString());
      setWhitelistedIPs(response.result || []);
    } catch (error) {
      console.error('Failed to load whitelisted IPs:', error);
      setError('Failed to load whitelisted IPs');
    } finally {
      setLoading(false);
    }
  };

  const detectPublicIP = async () => {
    setDetectingIP(true);
    try {
      const response = await apiService.detectPublicIP();
      setCurrentPublicIP(response.result?.ip_address || null);
    } catch (error) {
      console.error('Failed to detect public IP:', error);
      setCurrentPublicIP(null);
    } finally {
      setDetectingIP(false);
    }
  };

  const handleAddCurrentIP = () => {
    if (currentPublicIP) {
      setNewIPLabel('Current Location');
      setUseManualIP(false);
      setManualIP('');
      setShowAddModal(true);
    }
  };

  const handleAddManualIP = () => {
    setNewIPLabel('');
    setUseManualIP(true);
    setManualIP('');
    setShowAddModal(true);
  };

  const handleAddIP = async () => {
    const ipToAdd = useManualIP ? manualIP : currentPublicIP;

    if (!ipToAdd || !newIPLabel.trim() || !serviceInfo?.pk) return;

    setIsSubmitting(true);
    try {
      await apiService.addLegacyVoiceWhitelistedIP(
        serviceInfo.pk.toString(),
        ipToAdd,
        newIPLabel
      );

      // Reload the IP list to get fresh data from server
      await loadWhitelistedIPs();

      setShowAddModal(false);
      setNewIPLabel('');
      setManualIP('');
      setUseManualIP(false);
    } catch (error) {
      console.error('Failed to add IP:', error);
      setError('Failed to add IP to whitelist');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveIPRequest = (id: string, ipAddress: string) => {
    setDeleteTarget({ id, ip: ipAddress });
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !serviceInfo?.pk) return;

    try {
      await apiService.removeLegacyVoiceWhitelistedIP(
        serviceInfo.pk.toString(),
        deleteTarget.ip
      );

      // Reload the IP list to get fresh data from server
      await loadWhitelistedIPs();

      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to remove IP:', error);
      setError('Failed to remove IP from whitelist');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isIPWhitelisted = (ip: string) => {
    return whitelistedIPs.some(item => item.ip_address === ip);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading VoiceLinx Data</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  loadWhitelistedIPs();
                }}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-3xl font-bold text-gray-900">VoiceLinx Legacy</h1>
            <p className="mt-2 text-gray-600">Manage your legacy VoiceLinx phone system</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                loadServiceInfo();
                loadWhitelistedIPs();
              }}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2 text-gray-600" />
              <span className="text-gray-700">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'whitelist', name: 'IP Whitelist', icon: Shield },
            { id: 'settings', name: 'Settings', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-slate-500 text-slate-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-900">{totalExtensions}</p>
                  <p className="text-blue-700 font-medium">Total Extensions</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{whitelistedIPs.length}</p>
                  <p className="text-slate-700 font-medium">Whitelisted IPs</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <Shield className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-900">
                    {currentPublicIP && isIPWhitelisted(currentPublicIP) ? 'Yes' : 'No'}
                  </p>
                  <p className="text-green-700 font-medium">Current IP Allowed</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700 font-medium">Organization</p>
                  <p className="text-lg font-bold text-purple-900">{customerName}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Phone className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Organization</p>
                <p className="text-base font-semibold text-gray-900">{customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Customer ID</p>
                <p className="text-base font-semibold text-gray-900">{customerId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">System Type</p>
                <p className="text-base font-semibold text-gray-900">VoiceLinx Legacy PBX</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Service Status</p>
                <p className="text-base font-semibold text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {serviceInfo?.status === 'active' ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>

          {/* Current IP Status Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Globe className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Current Public IP</h3>
                  {detectingIP ? (
                    <div className="flex items-center space-x-2 mt-1">
                      <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                      <p className="text-sm text-gray-600">Detecting your IP address...</p>
                    </div>
                  ) : currentPublicIP ? (
                    <div className="mt-1">
                      <p className="text-2xl font-bold text-blue-900 font-mono">{currentPublicIP}</p>
                      {isIPWhitelisted(currentPublicIP) ? (
                        <div className="flex items-center space-x-2 mt-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <p className="text-sm text-green-700 font-medium">This IP is whitelisted - phones can register</p>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 mt-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <p className="text-sm text-amber-600 font-medium">This IP is not whitelisted - phones cannot register</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 mt-1">Unable to detect IP address</p>
                  )}
                </div>
              </div>
              {currentPublicIP && !isIPWhitelisted(currentPublicIP) && (
                <button
                  onClick={handleAddCurrentIP}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Whitelist IP
                </button>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Need to register phones?</strong> Your public IP address must be whitelisted to allow phone registration.
                Go to the <button onClick={() => setActiveTab('whitelist')} className="text-blue-600 hover:text-blue-700 underline font-medium">IP Whitelist</button> tab to manage allowed IP addresses.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* IP Whitelist Tab */}
      {activeTab === 'whitelist' && (
        <div className="space-y-8">
          {/* Current IP Detection Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Globe className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Your Current Public IP</h3>
                  {detectingIP ? (
                    <div className="flex items-center space-x-2 mt-1">
                      <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                      <p className="text-sm text-gray-600">Detecting your IP address...</p>
                    </div>
                  ) : currentPublicIP ? (
                    <div className="mt-1">
                      <p className="text-2xl font-bold text-blue-900 font-mono">{currentPublicIP}</p>
                      {isIPWhitelisted(currentPublicIP) ? (
                        <div className="flex items-center space-x-2 mt-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <p className="text-sm text-green-700 font-medium">This IP is already whitelisted</p>
                        </div>
                      ) : (
                        <p className="text-sm text-amber-600 mt-2">This IP is not whitelisted yet</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-red-600 mt-1">Unable to detect IP address</p>
                  )}
                </div>
              </div>
              {currentPublicIP && !isIPWhitelisted(currentPublicIP) && (
                <button
                  onClick={handleAddCurrentIP}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add This IP
                </button>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Why whitelist your IP?</strong> Your public IP address must be whitelisted to register phones to the VoiceLinx PBX.
                Add your current location to allow phone registration from this network.
              </p>
            </div>
          </div>

          {/* Whitelisted IPs List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Whitelisted IP Addresses</h2>
              <div className="flex items-center space-x-3">
                <p className="text-sm text-gray-500">{whitelistedIPs.length} IP{whitelistedIPs.length !== 1 ? 's' : ''} whitelisted</p>
                <button
                  onClick={handleAddManualIP}
                  className="inline-flex items-center px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Manual IP
                </button>
              </div>
            </div>

            {whitelistedIPs.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Whitelisted IPs</h3>
                <p className="text-gray-600 mb-4">Add your public IP address to allow phone registration</p>
                {currentPublicIP && (
                  <button
                    onClick={handleAddCurrentIP}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Current IP ({currentPublicIP})
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Label
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Added
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Added By
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {whitelistedIPs.map((ip) => (
                      <tr key={ip.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Shield className="h-4 w-4 text-slate-500 mr-2" />
                            <span className="text-sm font-mono font-semibold text-gray-900">{ip.ip_address}</span>
                            {currentPublicIP === ip.ip_address && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <MapPin className="h-3 w-3 mr-1" />
                                Current
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{ip.label}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(ip.date_added)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{ip.added_by || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRemoveIPRequest(ip.id, ip.ip_address)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Remove IP"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h3>
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Settings panel coming soon</p>
            <p className="text-sm text-gray-500 mt-2">Advanced configuration options will be available here</p>
          </div>
        </div>
      )}

      {/* Add IP Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {useManualIP ? 'Add Manual IP to Whitelist' : 'Add IP to Whitelist'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setUseManualIP(false);
                    setManualIP('');
                    setNewIPLabel('');
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6">
              <div className="space-y-4">
                {/* IP Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IP Address <span className="text-red-500">*</span>
                  </label>
                  {useManualIP ? (
                    <>
                      <input
                        type="text"
                        value={manualIP}
                        onChange={(e) => setManualIP(e.target.value)}
                        placeholder="e.g., 192.168.1.100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter the IP address to whitelist</p>
                    </>
                  ) : (
                    <>
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-lg font-bold text-gray-900">
                        {currentPublicIP}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Your current public IP address</p>
                    </>
                  )}
                </div>

                {/* Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newIPLabel}
                    onChange={(e) => setNewIPLabel(e.target.value)}
                    placeholder="e.g., Home Office, Main Office, Remote Site"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    autoFocus={!useManualIP}
                  />
                  <p className="text-xs text-gray-500 mt-1">Give this location a descriptive name</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setUseManualIP(false);
                    setManualIP('');
                    setNewIPLabel('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddIP}
                  disabled={!newIPLabel.trim() || (useManualIP && !manualIP.trim()) || isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Add to Whitelist</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-50 to-rose-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Remove IP Address</h3>
                </div>
                <button
                  onClick={handleCancelDelete}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove this IP address from the whitelist?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500 mb-1">IP Address:</p>
                <p className="text-lg font-bold font-mono text-gray-900">{deleteTarget.ip}</p>
              </div>
              <p className="text-sm text-red-600">
                <strong>Warning:</strong> Phones registered from this IP will no longer be able to connect to the PBX.
              </p>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Remove IP</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegacyVoice;
